import React, { useMemo } from 'react';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { BaseButton, DatePicker } from '@enter/core';
import { StatementClaimCard } from './statement-claim-cards.types';
import { getStatementStatusTone } from './statement-claim-cards.utils';
import {
    StatementAmount,
    StatementCodeBadge,
    StatementDxBadge,
    StatementDxList,
    StatementStatusBadge,
} from './statement-ui';
import * as S from './statement-claim-cards.styles';

export interface StatementClaimCardsProps {
    cards: StatementClaimCard[];
    searchQuery: string;
    onSearchQueryChange: (value: string) => void;
    dosFilterFrom: Date | null;
    dosFilterTo: Date | null;
    dosFilterKey: number;
    onDosFilterFromChange: (value: Date | null) => void;
    onDosFilterToChange: (value: Date | null) => void;
    onClearFilters: () => void;
}

export const StatementClaimCards: React.FC<StatementClaimCardsProps> = props => {
    const {
        cards,
        searchQuery,
        onSearchQueryChange,
        dosFilterFrom,
        dosFilterTo,
        dosFilterKey,
        onDosFilterFromChange,
        onDosFilterToChange,
        onClearFilters,
    } = props;

    const resultNote = useMemo(() => {
        if (cards.length === 1) return '1 claim';
        return `${cards.length} claims`;
    }, [cards.length]);

    const hasFilter = !!searchQuery.trim() || !!dosFilterFrom || !!dosFilterTo;

    return (
        <>
            <S.Toolbar>
                <S.SearchField
                    value={searchQuery}
                    onChange={event => onSearchQueryChange(event.target.value)}
                    placeholder="Search claim #, CPT code, or service"
                />

                <S.DosGroup>
                    <S.DosLabel>Date of service</S.DosLabel>
                    <DatePicker
                        key={`statement-dos-from-${dosFilterKey}`}
                        value={dosFilterFrom ?? undefined}
                        onDateSelected={date => onDosFilterFromChange(date ?? null)}
                    />
                    <span style={{ color: '#8e9293' }}>–</span>
                    <DatePicker
                        key={`statement-dos-to-${dosFilterKey}`}
                        value={dosFilterTo ?? undefined}
                        onDateSelected={date => onDosFilterToChange(date ?? null)}
                    />
                </S.DosGroup>

                {hasFilter && (
                    <BaseButton variant="outlined" size="small" onClick={onClearFilters}>
                        <Icon path={mdiClose} size={0.7} style={{ marginRight: 4 }} />
                        Clear
                    </BaseButton>
                )}

                <S.ResultNote>{resultNote}</S.ResultNote>
            </S.Toolbar>

            {cards.length === 0 ? (
                <S.Empty>No claims match your filters.</S.Empty>
            ) : (
                <S.CardList>
                    {cards.map(card => {
                        const metaParts = [
                            card.dosLabel,
                            card.linesCount > 0 ? `${card.linesCount} services` : undefined,
                            card.providersLabel ? `Providers ${card.providersLabel}` : undefined,
                            card.payer,
                        ].filter(Boolean);

                        return (
                            <S.ClaimCollapsible
                                key={card.id}
                                defaultOpen={false}
                                titleComponent={
                                    <S.HeadContent>
                                        <S.HeadMain>
                                            <S.HeadTitleRow>
                                                <S.ClaimNumber>Claim #{card.num}</S.ClaimNumber>
                                                {card.statusLabel && (
                                                    <StatementStatusBadge
                                                        tone={getStatementStatusTone(card.statusLabel)}>
                                                        {card.statusLabel}
                                                    </StatementStatusBadge>
                                                )}
                                                {card.patientOwes && card.oweAmount && (
                                                    <StatementStatusBadge tone="danger">
                                                        Patient owes {card.oweAmount}
                                                    </StatementStatusBadge>
                                                )}
                                            </S.HeadTitleRow>
                                            {metaParts.length > 0 && (
                                                <S.HeadMeta>{metaParts.join(' · ')}</S.HeadMeta>
                                            )}
                                        </S.HeadMain>
                                        <S.HeadBalance>
                                            <S.BalanceLabel>Balance on claim</S.BalanceLabel>
                                            <S.BalanceValue emphasis={card.balanceValue > 0}>
                                                {card.balance}
                                            </S.BalanceValue>
                                        </S.HeadBalance>
                                    </S.HeadContent>
                                }>
                                <S.CardBody>
                                    <S.ServicesPanel>
                                        <S.SectionLabel>Services rendered</S.SectionLabel>
                                        {card.lines.length === 0 ? (
                                            <S.HeadMeta>No services found</S.HeadMeta>
                                        ) : (
                                            card.lines.map(line => (
                                                <S.ServiceLine key={line.id}>
                                                    {line.code ? (
                                                        <StatementCodeBadge>{line.code}</StatementCodeBadge>
                                                    ) : (
                                                        <S.HeadMeta>—</S.HeadMeta>
                                                    )}
                                                    <S.ServiceMain>
                                                        <S.ServiceTitle>{line.description || '—'}</S.ServiceTitle>
                                                        {line.diagnosisCodes.length > 0 && (
                                                            <StatementDxList>
                                                                {line.diagnosisCodes.map(code => (
                                                                    <StatementDxBadge key={code}>
                                                                        {code}
                                                                    </StatementDxBadge>
                                                                ))}
                                                            </StatementDxList>
                                                        )}
                                                    </S.ServiceMain>
                                                    <S.ServiceCharge>{line.charge || '—'}</S.ServiceCharge>
                                                </S.ServiceLine>
                                            ))
                                        )}
                                    </S.ServicesPanel>

                                    <S.SettlementPanel>
                                        <S.SectionLabel>How this claim was settled</S.SectionLabel>
                                        <S.FlowList>
                                            {card.flowRows.map(row => (
                                                <S.FlowRow key={row.id} dividerTop={row.dividerTop}>
                                                    <S.FlowLabel emphasized={row.emphasized}>
                                                        {row.label}
                                                    </S.FlowLabel>
                                                    <StatementAmount
                                                        tone={row.tone}
                                                        style={{
                                                            fontSize: row.emphasized ? 16 : 14,
                                                            fontWeight: row.emphasized ? 600 : 500,
                                                        }}>
                                                        {row.amount}
                                                    </StatementAmount>
                                                </S.FlowRow>
                                            ))}
                                        </S.FlowList>
                                    </S.SettlementPanel>
                                </S.CardBody>
                            </S.ClaimCollapsible>
                        );
                    })}
                </S.CardList>
            )}
        </>
    );
};
